import { Link } from "@components/shared/core/Link";
import { ResultOf, readFragment } from "gql.tada";
import { LinkFragment } from "@queries/fragments/LinkFragment";
import { CaseStudyCardSectionFragment } from "@queries/fragments/CaseStudyCardSectionFragment";
import { IconChevronDown } from "@hawkins/assets";
import { MarkdownLite } from "@components/shared/MarkdownLite";
import { AnimatedSplitLines } from "@components/shared/AnimatedSplitLines";
import { animate, MotionValue, useMotionValue } from "motion/react";
import { useEffect, useState } from "react";
import { useTranslation } from "@services/i18n";
import { OBELIX_CTA_KEYS } from "@utils/obelixKeys";

type CaseStudyCardItem = NonNullable<
  NonNullable<
    ResultOf<typeof CaseStudyCardSectionFragment>["caseStudyCardsCollection"]
  >["items"][number]
>;

type PartnerLogos = NonNullable<CaseStudyCardItem["partnerLogosCollection"]>["items"];

export const CaseStudyCard = ({ card }: { card: CaseStudyCardItem }) => {
  const ctaLink = card.ctaLink ? readFragment(LinkFragment, card.ctaLink) : null;
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  // 1 = overlay clipped to bottom (idle), 0 = overlay fully visible (hovered).
  // Animating 1 -> 0 reveals the overlay bottom-up via the clipPath variant.
  const revealValue = useMotionValue(1);

  useEffect(() => {
    const controls = animate(revealValue, isHovered ? 0 : 1, {
      duration: 0.4,
      ease: "easeOut",
    });
    return () => controls.stop();
  }, [isHovered, revealValue]);

  return (
    <article className="set-current-palette-blue rounded-xl overflow-hidden flex flex-col h-full">
      {/* Top: image + tags (not clickable) */}
      <div className="relative h-[350px] p-4 flex flex-col items-start justify-end shrink-0">
        {card.posterImage?.url && (
          <>
            <img
              src={card.posterImage.url}
              alt={card.posterImage.title ?? ""}
              className="absolute inset-0 size-full object-cover"
            />
            <div aria-hidden className="absolute inset-0 bg-neutral-black/40" />
          </>
        )}
        {card.tagsCollection?.items && card.tagsCollection.items.length > 0 && (
          <ul className="relative flex gap-[10px] items-start flex-wrap">
            {card.tagsCollection.items.map((tag) =>
              tag ? (
                <li
                  key={tag.sys.id}
                  className="h-10 px-3 py-[11px] rounded-lg bg-neutral-cream/15 backdrop-blur-[25px] flex items-center"
                >
                  <span className="text-neutral-cream text-body-regular tracking-[-0.14px] whitespace-nowrap">
                    {tag.tagLabel}
                  </span>
                </li>
              ) : null
            )}
          </ul>
        )}
      </div>

      {/* Bottom: partner logo + headline + CTA (clickable as a whole) */}
      {ctaLink ? (
        <Link
          link={ctaLink}
          className="group block bg-neutral-cream/15 p-8 flex-1 flex flex-col justify-between gap-[4.5rem] min-h-0 no-underline"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <CaseStudyCardBottomContent
            headline={card.headline}
            partnerLogos={card.partnerLogosCollection?.items}
            ctaText={ctaLink.text ?? t(OBELIX_CTA_KEYS.readMore.obelixKey, OBELIX_CTA_KEYS.readMore.label)}
            revealValue={revealValue}
          />
        </Link>
      ) : (
        <div className="bg-neutral-cream/15 p-8 flex-1 flex flex-col justify-between gap-[4.5rem] min-h-0">
          <CaseStudyCardBottomContent
            headline={card.headline}
            partnerLogos={card.partnerLogosCollection?.items}
            ctaText={null}
            revealValue={revealValue}
          />
        </div>
      )}
    </article>
  );
};

const CaseStudyCardBottomContent = ({
  headline,
  partnerLogos,
  ctaText,
  revealValue,
}: {
  headline: string | null | undefined;
  partnerLogos: PartnerLogos | undefined;
  ctaText: string | null;
  revealValue: MotionValue<number>;
}) => {
  return (
    <>
      {partnerLogos && partnerLogos.length > 0 && (
        <ul className="flex gap-4 items-center shrink-0">
          {partnerLogos.map((logo, i) =>
            logo?.url ? (
              <li key={i}>
                <img
                  src={logo.url}
                  alt={logo.title ?? ""}
                  className="h-[65px] w-auto object-contain"
                />
              </li>
            ) : null
          )}
        </ul>
      )}
      {headline && (
        <p className="text-neutral-cream font-['Netflix_Sans_Title'] text-[40px] dsk:text-[50px] leading-[0.95] tracking-[-0.5px] font-medium [&_em]:not-italic [&_em]:text-palette-current-highlight [&_em]:mr-0">
          <AnimatedSplitLines
            value={revealValue}
            animationType="clipPath"
            underlyingTextClassNames="text-palette-current-highlight [&_em]:!text-neutral-cream"
          >
            <MarkdownLite>{headline}</MarkdownLite>
          </AnimatedSplitLines>
        </p>
      )}
      {ctaText && (
        <div className="flex items-center gap-[22px] shrink-0">
          <span className="text-palette-current-highlight text-button-large transition-colors duration-300 group-hover:text-neutral-cream">
            {ctaText}
          </span>
          <IconChevronDown
            size="standard"
            className="-rotate-90 text-palette-current-highlight transition duration-300 group-hover:text-neutral-cream group-hover:translate-x-1"
          />
        </div>
      )}
    </>
  );
};
